import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import { BUSINESS_TYPES } from '../../onboarding/components/BusinessTypeStep';
import Icon from '../../../components/AppIcon';
import toast from 'react-hot-toast';

// Service types per business type (inline — no dependency on seedBusinessData)
const SERVICE_TYPES = {
  plumbing: ['Emergency Repair', 'Leak Detection', 'Pipe Installation', 'Drain Cleaning', 'Water Heater', 'Fixture Installation', 'Sewer Line', 'Backflow Prevention'],
  hvac: ['AC Repair', 'Heating Repair', 'Installation', 'Maintenance', 'Duct Cleaning', 'Thermostat', 'Emergency Service', 'Seasonal Tune-up'],
  electrical: ['Panel Upgrade', 'Wiring', 'Outlet Installation', 'Lighting', 'EV Charger', 'Generator', 'Smart Home', 'Emergency'],
  handyman: ['Furniture Assembly', 'TV Mounting', 'Door Repair', 'Window Repair', 'Drywall Patch', 'Caulking & Sealing', 'Shelf Installation', 'Gutter Cleaning', 'Deck Repair', 'Fence Repair', 'Tile Repair', 'General Repairs', 'Painting Touch-up', 'Weatherstripping', 'Lock & Hardware'],
  cleaning: ['Deep Clean', 'Regular Maintenance', 'Move-in/Move-out', 'Post-Construction', 'Window Cleaning', 'Carpet Cleaning', 'Office Cleaning', 'Airbnb Turnover'],
  landscaping: ['Lawn Mowing', 'Landscaping Design', 'Tree Trimming', 'Irrigation', 'Mulching', 'Fertilization', 'Snow Removal', 'Hardscaping'],
  pest_control: ['General Inspection', 'Rodent Control', 'Termite Treatment', 'Bed Bug Treatment', 'Mosquito Control', 'Ant Treatment', 'Quarterly Service'],
  roofing: ['Roof Repair', 'Full Replacement', 'Inspection', 'Gutter Cleaning', 'Storm Damage', 'Flat Roof', 'Skylight Installation'],
  painting: ['Interior Painting', 'Exterior Painting', 'Cabinet Painting', 'Deck Staining', 'Wallpaper Removal', 'Commercial Painting', 'Touch-up'],
  pool_service: ['Weekly Cleaning', 'Chemical Balance', 'Filter Cleaning', 'Equipment Repair', 'Opening/Closing', 'Leak Detection', 'Resurfacing'],
  moving: ['Local Move', 'Long-Distance Move', 'Commercial Move', 'Packing Service', 'Unpacking Service', 'Storage', 'Piano/Specialty Move', 'Junk Removal'],
  general_contractor: ['Kitchen Remodel', 'Bathroom Remodel', 'Room Addition', 'Basement Finishing', 'Deck Building', 'Roofing', 'Siding', 'Flooring'],
  mobile_detailing: ['Basic Wash & Vacuum', 'Full Interior Detail', 'Full Exterior Detail', 'Full Detail (Interior + Exterior)', 'Paint Correction', 'Ceramic Coating', 'Paint Protection Film (PPF)', 'Headlight Restoration', 'Engine Bay Cleaning', 'Odor Elimination', 'Leather Conditioning', 'Window Tinting', 'Fleet Detailing', 'Boat/RV Detailing', 'Showroom Prep'],
  home_automation: [
    'Smart Home Consultation',
    'Whole-Home Automation Install',
    'Lighting Control System',
    'Home Theater & AV Setup',
    'Security Camera Installation',
    'Alarm & Intrusion System',
    'Access Control & Smart Locks',
    'Video Doorbell Installation',
    'Home Network & Wi-Fi Setup',
    'Structured Wiring & Low-Voltage',
    'Business Network Installation',
    'Managed IT / Network Support',
    'Smart Thermostat & HVAC Control',
    'Voice Control Integration (Alexa/Google)',
    'Motorized Shades & Blinds',
    'Whole-Home Audio',
    'EV Charger Smart Integration',
    'System Upgrade & Expansion',
    'Remote Monitoring Setup',
    'Annual Maintenance & Support',
  ],
  other: ['Consultation', 'Service Call', 'Installation', 'Repair', 'Maintenance', 'Inspection'],
};

const ChangeBusinessTypeTab = () => {
  const { organization, refreshOrganization } = useAuth();
  const [selectedType, setSelectedType] = useState(organization?.business_type || 'other');
  const [isLoading, setIsLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Keep selector in sync when organization refreshes from DB
  useEffect(() => {
    if (organization?.business_type) {
      setSelectedType(organization?.business_type);
    }
  }, [organization?.business_type]);

  const currentType = BUSINESS_TYPES?.find((b) => b?.id === organization?.business_type);
  const newType = BUSINESS_TYPES?.find((b) => b?.id === selectedType);

  const handleApply = () => {
    if (selectedType === organization?.business_type) {
      toast?.error('This is already your current business type.');
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = async () => {
    setShowConfirm(false);
    setIsLoading(true);

    const orgId = organization?.id;
    const bt = selectedType || 'other';
    const serviceTypes = SERVICE_TYPES?.[bt] || SERVICE_TYPES?.other;

    try {
      // Get the current session token to authenticate the edge function call
      const { data: { session }, error: sessionError } = await supabase?.auth?.getSession();

      if (sessionError || !session) {
        toast?.error('Session error — please log in again.');
        return;
      }

      const supabaseUrl = import.meta.env?.VITE_SUPABASE_URL;

      // Call edge function which uses service role key to bypass RLS
      const response = await fetch(`${supabaseUrl}/functions/v1/update-business-type`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          org_id: orgId,
          business_type: bt,
          service_types: serviceTypes,
        }),
      });

      const result = await response?.json();

      if (!response?.ok || result?.error) {
        toast?.error(`Update error: ${result?.error || 'Unknown error'}`);
        return;
      }

      // Force-refresh organization from DB so UI reflects the saved values
      await refreshOrganization();

      // Sync local selector to the confirmed new type
      setSelectedType(bt);

      toast?.success(`Business type changed to ${newType?.name}! Service types updated.`);
    } catch (err) {
      console.error('Change business type error:', err);
      toast?.error(`Unexpected error: ${err?.message || 'Please try again.'}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Current type banner */}
      <div className="flex items-center space-x-3 p-4 bg-primary/5 border border-primary/20 rounded-xl">
        <span className="text-2xl">{currentType?.icon || '⚙️'}</span>
        <div>
          <p className="text-sm text-muted-foreground">Current business type</p>
          <p className="font-semibold text-foreground">{currentType?.name || 'Other'}</p>
        </div>
      </div>

      {/* Warning */}
      <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-xl dark:bg-amber-950/20 dark:border-amber-800">
        <Icon name="AlertTriangle" size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-amber-800 dark:text-amber-400">
          <p className="font-semibold mb-1">Changing business type will update:</p>
          <ul className="list-disc list-inside space-y-0.5 text-amber-700 dark:text-amber-500">
            <li>Service types in the Jobs dropdown</li>
            <li>Business type label across the CRM</li>
          </ul>
          <p className="mt-2 font-medium">Your existing leads, contacts, jobs, and deals will NOT be deleted.</p>
        </div>
      </div>

      {/* Business type selector */}
      <div>
        <h3 className="text-sm font-semibold text-foreground mb-3">Select your new business type</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {BUSINESS_TYPES?.map((type) => {
            const isSelected = selectedType === type?.id;
            const isCurrent = organization?.business_type === type?.id;
            return (
              <button
                key={type?.id}
                onClick={() => setSelectedType(type?.id)}
                className={`relative flex flex-col items-center text-center p-4 rounded-xl border-2 transition-all duration-200 hover:shadow-md ${
                  isSelected
                    ? 'border-primary bg-primary/5 shadow-md'
                    : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                {isCurrent && (
                  <span className="absolute top-1.5 left-1.5 text-[10px] font-bold bg-muted text-muted-foreground px-1.5 py-0.5 rounded-full leading-tight">
                    Current
                  </span>
                )}
                {isSelected && !isCurrent && (
                  <div className="absolute top-2 right-2 w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                )}
                <span className="text-3xl mb-2">{type?.icon}</span>
                <span className={`text-sm font-semibold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                  {type?.name}
                </span>
                <span className="text-xs text-muted-foreground mt-1 leading-tight">{type?.desc}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Apply button */}
      <div className="flex justify-end pt-2">
        <button
          onClick={handleApply}
          disabled={isLoading || selectedType === organization?.business_type}
          className="flex items-center space-x-2 px-6 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span>Updating...</span>
            </>
          ) : (
            <>
              <Icon name="RefreshCw" size={16} />
              <span>Apply New Business Type</span>
            </>
          )}
        </button>
      </div>

      {/* Confirmation modal */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-card border border-border rounded-2xl p-6 max-w-md w-full shadow-xl">
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="AlertTriangle" size={20} className="text-amber-600" />
              </div>
              <h3 className="text-lg font-bold text-foreground">Confirm Business Type Change</h3>
            </div>
            <p className="text-sm text-muted-foreground mb-2">
              You are switching from <strong className="text-foreground">{currentType?.name}</strong> to{' '}
              <strong className="text-foreground">{newType?.name}</strong>.
            </p>
            <p className="text-sm text-muted-foreground mb-6">
              Service types in the Jobs dropdown will be updated to{' '}
              <strong className="text-foreground">{newType?.name}</strong>-specific defaults.
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowConfirm(false)}
                className="flex-1 px-4 py-2.5 border border-border rounded-xl text-sm font-medium text-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
              >
                Yes, Switch to {newType?.name}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeBusinessTypeTab;
